/**
 * Audit Log Viewer Component
 * Display configuration change history
 */

import React from 'react';
import { Card } from '@/components/ui/Card';
import { Loading } from '@/components/common';
import { History, AlertCircle } from 'lucide-react';
import { useAuditLogs } from '../hooks/useSystemConfig';
import { useI18n } from '@/i18n';

interface AuditLogViewerProps {
  type: string;
}

export const AuditLogViewer: React.FC<AuditLogViewerProps> = ({ type }) => {
  const { messages } = useI18n();
  const t = messages.systemSettings.auditLog;
  const { data, isLoading, error } = useAuditLogs(type);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  };

  const getActionBadge = (action: string) => {
    const colors: Record<string, string> = {
      create: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800',
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return <Loading message={t?.loading} />;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>{t?.loadFailed}: {error.message}</span>
      </div>
    );
  }

  const logs = data?.logs || [];

  if (logs.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
        <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">{t?.noLogs}</p>
        <p className="text-sm text-gray-500 mt-1">
          {t?.noLogsHint}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500 mb-4">
        {t?.showing?.replace('{current}', String(logs.length)).replace('{total}', String(data?.total || 0))}
      </p>

      {logs.map((log) => (
        <Card key={log.id} className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-2 py-1 text-xs font-medium rounded ${getActionBadge(log.action)}`}>
                  {log.action.toUpperCase()}
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {log.changed_by}
                </span>
                <span className="text-xs text-gray-500">
                  {formatDate(log.created_at)}
                </span>
              </div>

              <div className="text-sm text-gray-600 space-y-1">
                {log.old_version !== undefined && log.new_version !== undefined && (
                  <p>
                    {t?.version}: <span className="text-red-600">{log.old_version}</span>
                    {' → '}
                    <span className="text-green-600">{log.new_version}</span>
                  </p>
                )}

                {log.change_reason && (
                  <p>
                    <span className="font-medium">{t?.reason}:</span> {log.change_reason}
                  </p>
                )}

                {log.ip_address && (
                  <p className="text-xs text-gray-400">
                    {t?.ip}: {log.ip_address}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};
